import { Reveal, Eyebrow } from "./Reveal";
import { CONTACT, IMAGES } from "../data/content";
import { MessageCircle, Mail, MapPin, Instagram, Linkedin } from "lucide-react";

const QR_CARDS = [
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin, image: IMAGES.qrLinkedin, href: CONTACT.linkedinLink, handle: CONTACT.linkedin, action: "connect with", caption: "Connect with us on LinkedIn" },
  { id: "whatsapp", label: "WhatsApp Business", Icon: MessageCircle, image: IMAGES.qrWhatsapp, href: CONTACT.whatsappLink, handle: CONTACT.whatsapp, action: "message", caption: "Scan to start a conversation" },
  { id: "instagram", label: "Instagram", Icon: Instagram, image: IMAGES.qrInstagram, href: CONTACT.instagramLink, handle: CONTACT.instagram, action: "follow", caption: "Follow us on Instagram" },
];

const ContactIntro = () => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
    <Reveal className="lg:col-span-7">
      <Eyebrow>Contact Us</Eyebrow>
      <h2 className="f-display text-4xl sm:text-5xl lg:text-[4rem] text-[#0B2A5B] tracking-tight mt-4 max-w-3xl leading-[1.05]">
        Let&rsquo;s build what <span className="italic text-[#1E5FE0]">endures.</span>
      </h2>
      <p className="mt-6 max-w-xl text-base text-slate-600 leading-relaxed">
        Every engagement begins with a candid conversation. Reach us directly —
        no forms, no gatekeepers.
      </p>
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-5">
      <div className="bg-white rounded-2xl border border-[#0B2A5B]/10 p-8 flex items-center justify-center shadow-[0_24px_60px_-30px_rgba(11,42,91,0.25)]">
        <img
          src={IMAGES.logoFull}
          alt="Synergy Next — Strategy. People. Impact."
          data-testid="contact-logo"
          className="w-full max-w-[260px] h-auto"
        />
      </div>
    </Reveal>
  </div>
);

const DirectChannels = () => (
  <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
    <Reveal>
      <a
        href={CONTACT.whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="contact-whatsapp-button"
        className="group flex items-center justify-between bg-[#0B2A5B] rounded-2xl p-8 lg:p-10 hover:bg-[#1E5FE0] transition-colors duration-500 hover:-translate-y-1 hover:shadow-[0_28px_60px_-24px_rgba(30,95,224,0.6)]"
      >
        <div>
          <p className="f-label text-xs uppercase tracking-[0.2em] text-white/60">WhatsApp Business</p>
          <p className="f-display text-2xl lg:text-3xl text-white mt-3">{CONTACT.whatsapp}</p>
          <p className="text-sm text-white/60 mt-2">Direct line to our leadership team</p>
        </div>
        <span className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
          <MessageCircle className="w-6 h-6 text-white" />
        </span>
      </a>
    </Reveal>
    <Reveal delay={0.1}>
      <a
        href={CONTACT.emailLink}
        data-testid="contact-email-button"
        className="group flex items-center justify-between bg-white rounded-2xl border border-[#0B2A5B]/10 p-8 lg:p-10 hover:border-[#1E5FE0]/50 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(11,42,91,0.4)]"
      >
        <div>
          <p className="f-label text-xs uppercase tracking-[0.2em] text-[#1E5FE0]">Email</p>
          <p className="f-display text-2xl lg:text-3xl text-[#0B2A5B] mt-3 break-all">{CONTACT.email}</p>
          <p className="text-sm text-slate-500 mt-2">We respond personally, promptly</p>
        </div>
        <span className="w-14 h-14 rounded-full bg-[#F2F6FF] flex items-center justify-center group-hover:bg-[#1E5FE0] transition-colors duration-500">
          <Mail className="w-6 h-6 text-[#1E5FE0] group-hover:text-white transition-colors duration-500" />
        </span>
      </a>
    </Reveal>
  </div>
);

const AddressCard = () => (
  <Reveal delay={0.15}>
    <div className="mt-6 rounded-2xl border border-[#0B2A5B]/10 bg-white p-7 flex gap-4 items-start">
      <MapPin className="w-5 h-5 text-[#1E5FE0] mt-0.5 shrink-0" />
      <div>
        <p className="f-label text-xs uppercase tracking-[0.2em] text-[#0B2A5B]/50">Head Office</p>
        <p className="text-sm text-slate-700 mt-2 leading-relaxed" data-testid="contact-address">
          {CONTACT.address}
        </p>
        <p className="f-label text-xs uppercase tracking-[0.2em] text-[#1E5FE0] mt-3">Pan-India presence</p>
      </div>
    </div>
  </Reveal>
);

const QrCard = ({ q }) => (
  <a
    href={q.href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`contact-qr-${q.id}`}
    className="group rounded-2xl border border-[#0B2A5B]/10 bg-white p-7 flex flex-col items-center text-center hover:border-[#1E5FE0]/50 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(11,42,91,0.35)] transition-all duration-500"
  >
    <img src={q.image} alt={`Scan to ${q.action} Synergy Next on ${q.label}`} className="w-40 h-40 sm:w-44 sm:h-44 rounded-xl" />
    <p className="f-label text-xs uppercase tracking-[0.2em] text-[#0B2A5B]/50 mt-6 flex items-center gap-2">
      <q.Icon className="w-3.5 h-3.5 text-[#1E5FE0]" /> {q.label}
    </p>
    <p className="text-sm text-slate-700 mt-2 group-hover:text-[#1E5FE0] transition-colors break-all">{q.handle}</p>
    <p className="text-xs text-slate-500 mt-2">{q.caption}</p>
  </a>
);

const QrCards = () => (
  <Reveal delay={0.2}>
    <div className="mt-16">
      <Eyebrow>Let&rsquo;s stay connected</Eyebrow>
      <h3 className="f-display text-2xl sm:text-3xl text-[#0B2A5B] mt-3">
        Scan to <span className="italic text-[#1E5FE0]">connect.</span>
      </h3>
    </div>
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
      {QR_CARDS.map((q) => <QrCard key={q.id} q={q} />)}
    </div>
  </Reveal>
);

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="py-24 lg:py-36 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#1E5FE0]/10 blur-[110px]" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
      <ContactIntro />
      <DirectChannels />
      <AddressCard />
      <QrCards />
    </div>
  </section>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#0B2A5B] text-white py-14 relative overflow-hidden">
    <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-[#1E5FE0]/25 blur-[100px]" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
        <div>
          <div className="flex items-center gap-3">
            <span className="h-12 px-3 rounded-lg bg-white flex items-center justify-center">
              <img src={IMAGES.logoMark} alt="Synergy Next" data-testid="footer-logo" className="h-8 w-auto" />
            </span>
            <span className="font-sans text-base font-extrabold tracking-[0.04em]">
              SYNERGY<span className="text-[#7FA8FF]">NEXT</span>
            </span>
          </div>
          <p className="f-label text-xs uppercase tracking-[0.2em] text-[#7FA8FF] mt-3">Strategy. People. Impact.</p>
          <p className="text-sm text-white/50 mt-5 max-w-sm leading-relaxed">
            Carrying forward a 30-year legacy that began with Synergy Communications —
            building organisations that endure.
          </p>
        </div>
        <div className="text-sm text-white/60 space-y-2 lg:text-right">
          <p>{CONTACT.address}</p>
          <p>{CONTACT.email} &nbsp;·&nbsp; {CONTACT.whatsapp}</p>
        </div>
      </div>
      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div className="max-w-3xl" data-testid="footer-disclaimer">
          <p className="f-label text-xs uppercase tracking-[0.2em] text-[#7FA8FF] mb-2">Disclaimer</p>
          <p className="text-xs text-white/45 leading-relaxed">{CONTACT.disclaimer}</p>
        </div>
        <p className="f-label text-xs uppercase tracking-[0.2em] text-white/35 shrink-0">
          &copy; {new Date().getFullYear()} Synergy Next. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);
